/**
 * Theme selection and persistence.
 *
 * The initial application happens in `public/theme-init.js` before first paint. This module owns
 * everything after mount: switching, persisting, and following the system preference when the
 * user has not chosen explicitly.
 */

export const THEMES = [
  'light',
  'dark',
  'dim',
  'high-contrast-light',
  'high-contrast-dark',
] as const;

export type Theme = (typeof THEMES)[number];

/** Must match the key in `public/theme-init.js`. */
export const THEME_STORAGE_KEY = 'conduit.theme';

export interface ThemeOption {
  readonly id: Theme;
  readonly label: string;
  readonly description: string;
}

export const THEME_OPTIONS: readonly ThemeOption[] = [
  {
    id: 'light',
    label: 'Light',
    description: 'Default. Shared screens and daylight.',
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'Night shift and long sessions.',
  },
  {
    id: 'dim',
    label: 'Dim',
    description: 'Lower contrast dark, for extended overnight use.',
  },
  {
    id: 'high-contrast-light',
    label: 'High contrast (light)',
    description: 'WCAG AAA contrast.',
  },
  {
    id: 'high-contrast-dark',
    label: 'High contrast (dark)',
    description: 'WCAG AAA contrast.',
  },
];

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/**
 * The theme the document is currently rendering.
 *
 * Read from the DOM rather than from state, because `theme-init.js` set it before React existed
 * and the DOM is therefore the source of truth at mount.
 */
export function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  return isTheme(attr) ? attr : 'light';
}

/**
 * Applies a theme and persists the choice.
 *
 * Persistence is best-effort: storage throws in private browsing and when blocked by policy. A
 * failed write must not prevent the theme from applying — the user still gets what they asked
 * for this session.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore deliberately. See the note above.
  }
}

/** True when the user has explicitly chosen, rather than inheriting the system preference. */
export function hasExplicitPreference(): boolean {
  try {
    return isTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return false;
  }
}

/**
 * Follows the operating system while the user has no explicit preference.
 *
 * Returns an unsubscribe function. Once the user picks a theme we stop following — switching
 * their chosen theme out from under them at sunset would be worse than not following at all.
 */
export function followSystemPreference(): () => void {
  if (typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const dark = window.matchMedia('(prefers-color-scheme: dark)');
  const contrast = window.matchMedia('(prefers-contrast: more)');

  const sync = (): void => {
    if (hasExplicitPreference()) {
      return;
    }
    const next: Theme = contrast.matches
      ? dark.matches
        ? 'high-contrast-dark'
        : 'high-contrast-light'
      : dark.matches
        ? 'dark'
        : 'light';

    // Apply without persisting: this is inherited, not chosen.
    document.documentElement.setAttribute('data-theme', next);
  };

  dark.addEventListener('change', sync);
  contrast.addEventListener('change', sync);

  return () => {
    dark.removeEventListener('change', sync);
    contrast.removeEventListener('change', sync);
  };
}
